/**
 * Card Component
 *
 * Displays a card with title, body text, and an icon.
 * Integrates with the CMS system and supports feature flags, status flags,
 * and navigation with optional nesting.
 */

import { Icon } from '@iconify/react';
import { useRouter } from 'next/navigation';
import React from 'react';

import { CardComponentProps } from '@/@cms/types/props';
import { StudioRoutes } from '@/app/routes';
import { useSession } from '@/providers/enhanced-session';
import { Card, CardBody, CardHeader, Chip } from '@heroui/react';

/**
 * Determines the color for flag/status chips
 *
 * @param flag - Flag value from CMS
 * @returns Appropriate color for the status chip
 */
const getChipColor = (flag: string) => {
    switch (flag) {
        case 'Beta':
            return 'primary';
        case 'Alpha':
            return 'secondary';
        case 'New':
            return 'success';
        case 'Freemium':
            return 'warning';
        default:
            return 'default';
    }
};

/**
 * Card component that displays content items with navigation capabilities
 *
 * Features:
 * - Icon and title display
 * - Status flags (Beta, Alpha, New)
 * - Feature flag integration
 * - Click handling with navigation
 * - Template variable resolution
 *
 * @param props - Component properties from the CMS
 * @returns Rendered Card component
 */
const CardComponent: React.FC<CardComponentProps> = ({
    data,
    sectionName: _sectionName,
    onClick,
    resolveTemplate,
    className = '',
    isLoading = false,
    isDisabled = false,
}) => {
    const router = useRouter();
    const { data: session } = useSession();

    // Process template variables in card data if resolver provided
    const processedData = React.useMemo(() => {
        if (!resolveTemplate) return data;

        return {
            ...data,
            TitleText: resolveTemplate(data.TitleText),
            BodyText: data.BodyText ? resolveTemplate(data.BodyText) : '',
            Link: resolveTemplate(data.Link),
        };
    }, [data, resolveTemplate]);

    /**
     * Handle card click, navigating to the card's link
     * and calling the onClick callback if provided
     */
    const handleCardClick = () => {
        // Don't do anything if card is disabled
        if (isDisabled) {
            return;
        }

        // Call onClick handler if provided
        if (onClick) {
            onClick(processedData);
        }

        // If not a nested card or there's no link, do nothing further
        if (!processedData.Link || processedData.Link === '#') {
            return;
        }

        // Navigate to the link
        router.push(processedData.Link);
    };

    // Show loading state if needed
    if (isLoading) {
        return (
            <Card className={`h-64 animate-pulse p-4 ${className}`}>
                <CardHeader className="flex items-center justify-between">
                    <div className="h-8 w-1/3 rounded bg-default-200 dark:bg-default-100"></div>
                    <div className="h-6 w-16 rounded bg-default-200 dark:bg-default-100"></div>
                </CardHeader>
                <CardBody>
                    <div className="h-24 w-full rounded bg-default-200 dark:bg-default-100"></div>
                </CardBody>
            </Card>
        );
    }

    const isDaniCard = processedData.Link === StudioRoutes.STUDIO_AI;
    const isUnaffiliatedDani =
        isDaniCard &&
        session?.user?.hasStudioAIAccess !== true &&
        session?.user?.isUnaffiliated === true;
    const isTrialDani =
        isDaniCard &&
        session?.user?.hasStudioAIAccess !== true &&
        session?.user?.isUnaffiliated !== true &&
        session?.org?.hasClientStudioAIAccess === true;
    const showFlag =
        isUnaffiliatedDani || isTrialDani || (processedData.Flag && processedData.Flag !== 'None');
    const displayFlag = isUnaffiliatedDani
        ? 'Freemium'
        : isTrialDani
          ? 'Preview'
          : processedData.Flag === 'Preview' && session?.user?.isUnaffiliated
            ? 'Freemium'
            : processedData.Flag;

    return (
        <Card
            onPress={handleCardClick}
            className={`h-64 p-4 transition-transform ${
                isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-[1.02]'
            } ${className}`}
            isPressable={!isDisabled}
            isDisabled={isDisabled}
        >
            <CardHeader className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {/* Card icon - using dynamic lookup from IconName */}
                    <div className="rounded-full bg-foreground/10 p-1">
                        <Icon icon={`lucide:${processedData.TitleIcon}`} width={28} height={28} />
                    </div>

                    <h1 className="text-xlarge font-bold">{processedData.TitleText}</h1>
                </div>

                {/* Status flag chip */}
                {showFlag && (
                    <Chip
                        key={`${processedData.id}-${displayFlag}`}
                        color={
                            displayFlag === 'New' ? undefined : getChipColor(displayFlag as string)
                        }
                        classNames={
                            displayFlag === 'New'
                                ? { base: 'bg-mfour-green', content: 'text-white' }
                                : undefined
                        }
                    >
                        {displayFlag}
                    </Chip>
                )}
            </CardHeader>

            <CardBody>
                <p className="text-small text-foreground/50">{processedData.BodyText}</p>

                {/* Show nested indicator if applicable */}
                {processedData.isNested && (
                    <div className="mt-auto pt-4 text-mini text-primary">
                        <span className="flex items-center gap-1">
                            View Details
                            <Icon icon="lucide:arrow-right" width={14} height={14} />
                        </span>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default CardComponent;
